import { css, Rule } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'

import { useTheme, Theme } from '../../theme'
import { isKey } from '../../utils'
import { UiVariant } from '../../types'

import { ChevronIcon } from '../chevron-icon'
import { Flex } from '../flex'
import { Loader } from '../loader'

import getStyles from './styles'

export type ButtonVariant = UiVariant | 'clear' | 'clearInverted' | 'hollowInverted' | 'link'

export type ButtonSize = 'xSmall' | 'small' | 'large' | 'flat'

type ButtonChevronDirection = 'up' | 'down' | 'left' | 'right'

const variants: ButtonVariant[] = [
  'primary',
  'secondary',
  'cta',
  'negative',
  'positive',
  'clear',
  'clearInverted',
  'hollowInverted',
  'link',
  'alert',
  'brand',
]
const sizes: ButtonSize[] = ['xSmall', 'small', 'large', 'flat']
const chevronDirections: ButtonChevronDirection[] = ['up', 'down', 'left', 'right']

interface IButtonLoaderProps {
  visible?: boolean
  hasSiblings?: boolean
  variant?: ButtonVariant | null
}

function ButtonLoader({ visible, hasSiblings, variant }: IButtonLoaderProps) {
  const styles = getStyles(useTheme())

  return (
    <span
      {...css(
        ...[
          styles.processing.base,
          visible && styles.processing.visible,
          visible && hasSiblings && styles.processing.visibleWithSiblings,
        ]
      )}
    >
      {visible ? (
        <Loader
          showAfterMs={0}
          size="small"
          style={[styles.loader.base]}
          theme={
            variant && ['link', 'clear'].includes(variant)
              ? 'blue'
              : variant && variant === 'secondary'
              ? 'dark'
              : 'light'
          }
        />
      ) : null}
    </span>
  )
}

ButtonLoader.propTypes = {
  visible: PropTypes.bool,
  hasSiblings: PropTypes.bool,
  variant: PropTypes.oneOf(variants),
}

export interface IButtonProps {
  variant?: ButtonVariant | null
  size?: ButtonSize | null
  disableOnProcessing?: boolean | null

  component?: string | React.ComponentType<{ ref?: React.RefObject<{}> }>
  children?: React.ReactNode
  processing?: boolean
  onClick?: () => void
  multiChoice?: boolean
  disabled?: boolean
  style?: Rule
  block?: boolean
  direction?: ButtonChevronDirection
  flush?: boolean
  innerRef?: React.RefObject<{}>
  hovered?: boolean
  outline?: boolean
  type?: 'submit' | 'reset' | 'button'
  minWidth?: number
}

export class Button extends React.Component<IButtonProps> {
  static propTypes = {
    component: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    children: PropTypes.node,
    processing: PropTypes.bool,
    onClick: PropTypes.func,
    variant: PropTypes.oneOf(variants),
    size: PropTypes.oneOf(sizes),
    multiChoice: PropTypes.bool,
    disabled: PropTypes.bool,
    style: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    block: PropTypes.bool,
    direction: PropTypes.oneOf(chevronDirections),
    flush: PropTypes.bool,
    disableOnProcessing: PropTypes.bool,
    innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    hovered: PropTypes.bool,
    outline: PropTypes.bool,
    type: PropTypes.oneOf(['submit', 'reset', 'button']),
    minWidth: PropTypes.number,
  }

  static displayName = 'Button'

  mounted?: boolean

  static defaultProps = {
    variant: 'primary',
    size: 'large',
    disableOnProcessing: true,
  }

  state = {
    showLoader: this.props.processing,
  }

  componentDidMount() {
    this.mounted = true
  }

  componentDidUpdate(prevProps: IButtonProps) {
    if (prevProps.processing !== this.props.processing) {
      if (this.showTimer) {
        clearTimeout(this.showTimer)
      }
      if (this.hideTimer) {
        clearTimeout(this.hideTimer)
      }

      if (this.props.processing) {
        this.showAt = new Date()
        this.showTimer = window.setTimeout(() => this.mounted && this.setState({ showLoader: true }), 500)
      } else {
        const delay = this.showAt ? Math.max(1500 - (new Date().getTime() - this.showAt.getTime()), 0) : 1500
        this.hideTimer = window.setTimeout(
          () => this.mounted && !this.props.processing && this.setState({ showLoader: false }),
          delay
        )
      }
    }
  }

  componentWillUnmount() {
    this.mounted = false
  }

  hideTimer?: number
  showTimer?: number
  showAt?: Date

  static SIZES = sizes
  static VARIANTS = variants

  render() {
    const {
      component,
      children,
      processing,
      onClick,
      variant = 'primary',
      outline = false,
      size = 'large',
      multiChoice,
      disabled,
      hovered,
      block,
      direction,
      flush,
      style: externalStyle,
      disableOnProcessing,
      innerRef,
      minWidth,
      ...additional
    } = this.props

    const buttonProps = {
      ...additional,
      disabled: disabled || (disableOnProcessing && processing),
      onClick,
      ref: innerRef,
    }

    const Component = component || ((buttonProps as any).href ? 'a' : 'button')

    const padding = size === 'xSmall' || (variant && ['clear', 'clearInverted', 'link'].includes(variant)) ? 0 : 30

    return (
      <Theme>
        {theme => {
          const styles = getStyles(theme)

          const chevronColors = {
            secondary: theme.colors.text.normal,
            clear: theme.colors.text.normal,
            link: theme.colors.accents.primary,
          }

          const style = [
            styles.button.base,
            isKey(styles.button.sizes, size) && styles.button.sizes[size],
            isKey(styles.button.variants, variant) && !disabled && styles.button.variants[variant].base,
            isKey(styles.button.variants, variant) &&
              !disabled &&
              hovered &&
              styles.button.variants[variant].base[':hover'],
            isKey(styles.button.variants, variant) && !disabled && outline && styles.button.variants[variant].outline,
            onClick && !disabled && styles.button.clickable,
            disabled && styles.button.disabled,
            multiChoice && styles.button.multiChoice,
            block && styles.button.block,
            flush && styles.button.flush,
            externalStyle,
          ]

          return (
            <Component {...buttonProps} {...css(style)}>
              <span
                {...css(
                  styles.innerWrapper.base,
                  size && styles.innerWrapper.sizes[size],
                  isKey(styles.innerWrapper.variants, variant) && styles.innerWrapper.variants[variant],
                  multiChoice && styles.innerWrapper.multiChoice,
                  minWidth !== undefined && {
                    minWidth: Math.max(0, minWidth - padding),
                  }
                )}
              >
                <span {...css(styles.contentWrapper.base)}>
                  {!(block && this.state.showLoader) && children}
                  <ButtonLoader hasSiblings={!block} variant={variant} visible={this.state.showLoader} />
                </span>
                {multiChoice && (
                  <ChevronIcon
                    color={
                      (isKey(chevronColors, variant) && chevronColors[variant]) || outline
                        ? theme.colors.text.normal
                        : theme.colors.text.light
                    }
                    direction={direction}
                  />
                )}
              </span>
            </Component>
          )
        }}
      </Theme>
    )
  }
}

export const ButtonGroup: React.FC<{
  block?: boolean
  children?: React.ReactNode
}> = props => {
  const styles = getStyles(useTheme())

  return (
    <Flex
      display={props.block ? 'flex' : 'inline-flex'}
      justifyContent="space-evenly"
      role="group"
      row
      style={styles.buttonGroup.base}
    >
      {props.children}
    </Flex>
  )
}

ButtonGroup.propTypes = {
  block: PropTypes.bool,
  children: PropTypes.node,
}

export const ButtonToolbar: React.FC<{
  block?: boolean
  children?: React.ReactNode
}> = props => {
  const styles = getStyles(useTheme())

  return (
    <Flex
      display={props.block ? 'flex' : 'inline-flex'}
      justifyContent="space-evenly"
      role="group"
      row
      style={styles.buttonToolbar.base}
    >
      {props.children}
    </Flex>
  )
}

ButtonToolbar.propTypes = {
  block: PropTypes.bool,
  children: PropTypes.node,
}
